/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import styled from 'styled-components';

import { LazyImage, colors } from '@crehana/ui';
import { EyeOutline, PlayArrow } from '@crehana/icons';
import { getImgxUrl } from '@crehana/utils';
import { useAmplitude } from '@crehana/analytics';
import { useMe } from '@crehana/web/dist/ReactContexts/AuthContext';

import type { ImgxParams } from 'Jsx/Types/ImgxParams';
import type { CourseCardProps } from 'Jsx/Catalog/components/CourseCardV3/types';

const ImageContainer = styled.div<{ lazyImage: boolean }>`
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0)
      linear-gradient(0deg, rgb(24, 27, 50) 0%, rgba(24, 27, 50, 0) 100%) repeat
      scroll 0% 0%;
    z-index: 0;
  }

  &:hover {
    .before-preview {
      opacity: 0;
    }
  }

  .before-preview {
    z-index: 3;
    opacity: 1;

    &:hover {
      opacity: 0;
      transition: ease-in 0.3s;
    }
  }

  .preview {
    z-index: 4;
    opacity: 0;
    &:hover {
      opacity: 1;
      transition: ease-in 0.3s;
    }
  }

  ${({ theme }) => theme.breakpoints.up('sm')} {
    height: ${({ theme, lazyImage }) =>
      !lazyImage ? theme.spacing[200] : 'unset'};
  }
`;

const Image: React.FC<Partial<CourseCardProps>> = props => {
  const {
    children,
    lazyImageProps,
    lazyImage = true,
    priority,
    hidePreview,
    beforePreview,
    source,
    product,
    ...rest
  } = props;
  const { me: user } = useMe();

  const imgxParams: ImgxParams = {
    w: 322,
    h: 219,
    ...rest.imgxParams,
  };

  const { logEvent } = useAmplitude();

  const showPreview = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();

    logEvent({
      event: 'Click on Trailer',
      data: {
        source,
        courseId: product?.id,
        isB2BUser: user?.isB2B || false,
        isPremiumUser: user?.isPremiumMember || false,
      },
    });

    if (beforePreview) {
      beforePreview();
    }
    if (window.showCoursePreview && !hidePreview) {
      window.showCoursePreview(props);
    } else if (rest.url) {
      window.location.href = rest.url;
    }
  };

  const onClickImage = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (props.customOnClickImage) {
      event.preventDefault();
      // only run if it come from the props
      props.customOnClickImage(event);
    }
  };

  return (
    <a
      href={rest.url ? rest.url : ''}
      onClick={onClickImage}
      className="block ImageComponent"
      arial-label={rest.title}
    >
      <ImageContainer
        className="relative ImageComponentWrapper"
        lazyImage={lazyImage}
      >
        {!hidePreview && (
          <>
            <div
              onClick={showPreview}
              onKeyPress={showPreview}
              className="absolute items-center justify-center hidden w-full h-full md:flex bg-base-main-80 preview"
            >
              <EyeOutline size={24} color="#fff" />
              <span className="ml-8 text-white tw-font-link2">Ver Trailer</span>
            </div>
            <div className="absolute items-center justify-center hidden w-full h-full md:flex before-preview">
              <div
                style={{
                  backgroundColor: 'rgba(75, 34, 244, 0.6)',
                }}
                className="flex items-center justify-center w-32 h-32 rounded-full"
              >
                <PlayArrow size={18} color={colors.white} />
              </div>
            </div>
          </>
        )}
        {priority ? (
          <div
            css={`
              background-image: url(${rest.imagePlaceholder});
              background-position: center;
              background-size: cover;
              padding-bottom: 70%;
            `}
            className="relative course-image"
          >
            <img
              src={getImgxUrl(
                rest.promoImageAnimated || rest.promoImage || '',
                {
                  auto: 'format',
                  q: 55,
                  ...imgxParams,
                },
              )}
              alt={rest.title}
              className="absolute inset-0 object-cover w-full h-full"
              decoding="async"
            />
          </div>
        ) : (
          <LazyImage
            placeholder={rest.imagePlaceholder}
            src={(rest.promoImageAnimated || rest.promoImage) as string}
            imgxParams={imgxParams}
            css={`
              height: auto;
              padding-bottom: 70%;
            `}
            alt={rest.title as string}
            className="course-image"
            {...lazyImageProps}
            imgClassName={`${lazyImageProps?.imgClassName || ''} object-cover`}
          />
        )}
        {React.Children.toArray(children).map(child =>
          React.cloneElement(child as React.ReactElement<any>, rest),
        )}
      </ImageContainer>
    </a>
  );
};

export default Image;
